import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Home, BadgePercent, ShoppingCart, MapPin, User,
  Motorbike, Package, Building, Truck,
  Users, ShieldUser, ClipboardList, Bell, LogIn,
} from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useCart } from "../context/CartContext";
import UserMenu from "./UserMenu";
import API from "../api/axiosConfig";

/* ── helpers ────────────────────────────────────────────────────── */
function getStoredUser() {
  try {
    const s = localStorage.getItem("user");
    if (s && s !== "undefined" && s !== "null") return JSON.parse(s);
  } catch { localStorage.removeItem("user"); }
  return null;
}

const base   = "flex flex-col items-center gap-0.5 transition-colors flex-1 min-w-0";
const idle   = "text-zinc-500 hover:text-zinc-300";
const active = "text-supernova-400";
const lbl    = "text-[10px] font-medium leading-none mt-0.5";

/* Botón normal */
function Btn({ icon: Icon, label, to, exact = false, onClick }) {
  const location = useLocation();
  const navigate = useNavigate();
  const on = exact ? location.pathname === to : to && location.pathname.startsWith(to);
  return (
    <button
      onClick={onClick ?? (() => navigate(to))}
      className={`${base} ${on ? active : idle}`}
    >
      <Icon size={22} />
      <span className={lbl}>{label}</span>
    </button>
  );
}

/* Botón alertas/notificaciones con badge */
function NotifBtn({ notifCount }) {
  const location = useLocation();
  const navigate = useNavigate();
  const on = location.pathname.startsWith("/notifications");
  return (
    <button onClick={() => navigate("/notifications")} className={`relative ${base} ${on ? active : idle}`}>
      <Bell size={22} />
      {notifCount > 0 && (
        <span className="absolute -top-1 right-0 bg-red-500 text-white text-[9px] font-bold min-w-[15px] h-[15px] px-0.5 flex items-center justify-center rounded-full animate-pulse">
          {notifCount > 99 ? "99+" : notifCount}
        </span>
      )}
      <span className={lbl}>Alertas</span>
    </button>
  );
}

/* Botón cuenta con badge */
function AccountBtn({ notifCount, onClick }) {
  const location = useLocation();
  const on = location.pathname.startsWith("/myaccount") || location.pathname.startsWith("/myprofile");
  return (
    <button onClick={onClick} className={`relative ${base} ${on ? active : idle}`}>
      <User size={22} />
      {notifCount > 0 && (
        <span className="absolute -top-1 -right-0.5 bg-red-500 text-white text-[9px] font-bold min-w-[15px] h-[15px] px-0.5 flex items-center justify-center rounded-full">
          {notifCount}
        </span>
      )}
      <span className={lbl}>Cuenta</span>
    </button>
  );
}

/* Botón central elevado — panel principal del rol */
const panelColors = {
  amber:  { ring: "border-zinc-950", bg: "bg-supernova-500",  bgHov: "hover:bg-supernova-400",  icon: "text-black",     shadow: "shadow-supernova-500/40", lbl: "text-supernova-400" },
  blue:   { ring: "border-zinc-950", bg: "bg-blue-600",       bgHov: "hover:bg-blue-500",       icon: "text-white",     shadow: "shadow-blue-500/30",      lbl: "text-blue-400"     },
  violet: { ring: "border-zinc-950", bg: "bg-violet-600",     bgHov: "hover:bg-violet-500",     icon: "text-white",     shadow: "shadow-violet-500/30",    lbl: "text-violet-400"   },
  zinc:   { ring: "border-zinc-950", bg: "bg-zinc-700",       bgHov: "hover:bg-zinc-600",       icon: "text-zinc-200",  shadow: "shadow-black/40",         lbl: "text-zinc-300"     },
};

function CenterBtn({ icon: Icon, label, to, color = "amber" }) {
  const location = useLocation();
  const navigate = useNavigate();
  const on = location.pathname === to || location.pathname.startsWith(to + "/");
  const c  = panelColors[color];
  return (
    <button onClick={() => navigate(to)} className="flex flex-col items-center gap-0.5 flex-1 -mt-5">
      <div className={`w-14 h-14 rounded-[18px] flex items-center justify-center shadow-lg border-[3px] ${c.ring} ${c.bg} ${on ? "" : c.bgHov} transition-all ${on ? "scale-105 " + c.shadow : ""}`}>
        <Icon size={26} className={c.icon} />
      </div>
      <span className={`text-[10px] font-semibold leading-none mt-0.5 ${on ? c.lbl : "text-zinc-500"}`}>{label}</span>
    </button>
  );
}

/* Wrapper con safe-area */
function Bar({ children }) {
  return (
    <div
      className="fixed bottom-0 left-0 w-full bg-zinc-950 border-t border-zinc-800/80 lg:hidden z-[9999]"
      style={{ paddingLeft: "env(safe-area-inset-left,0px)", paddingRight: "env(safe-area-inset-right,0px)" }}
    >
      <div className="flex items-end justify-around px-1 pt-2 pb-2">
        {children}
      </div>
      <div style={{ height: "env(safe-area-inset-bottom,0px)" }} />
    </div>
  );
}

/* UserMenu drawer */
function Drawer({ open, onClose }) {
  const navigate = useNavigate();
  const user = getStoredUser();
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[10000]">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="absolute top-0 right-0 h-full w-[85%] max-w-sm bg-white shadow-2xl animate-slideInRight">
        <UserMenu
          user={user}
          logout={() => {
            ["token","accessToken","refreshToken","user"].forEach((k) => localStorage.removeItem(k));
            navigate("/");
            onClose();
          }}
          onClose={onClose}
        />
      </div>
    </div>
  );
}

/* Carrito flotante draggable */
function FloatingCart({ count, bottom = "bottom-24" }) {
  const navigate = useNavigate();
  if (count === 0) return null;
  return (
    <motion.button
      drag dragMomentum={false} dragElastic={0.1}
      dragConstraints={{ top: -(window.innerHeight - 160), bottom: -80, left: -(window.innerWidth / 2 - 80), right: window.innerWidth / 2 - 80 }}
      onClick={() => navigate("/cart")}
      className={`fixed ${bottom} left-1/2 -translate-x-1/2 z-40 flex items-center gap-1.5 bg-supernova-400 text-black font-bold text-xs px-4 py-2.5 rounded-2xl shadow-xl lg:hidden cursor-grab active:cursor-grabbing touch-none`}
    >
      <ShoppingCart size={15} /> {count} en carrito
    </motion.button>
  );
}

/* Botón flotante de la ruleta — solo aparece si hay giros disponibles, con
   badge pulsante para avisar sin ocupar una de las 5 posiciones fijas de la
   barra (ya están todas usadas por rol). */
function FloatingWheel({ spins }) {
  const navigate = useNavigate();
  if (!spins) return null;
  return (
    <button
      onClick={() => navigate("/ruleta")}
      className="fixed top-20 right-3 z-40 flex items-center justify-center w-12 h-12 bg-white rounded-full shadow-xl border border-gray-100 lg:hidden"
    >
      <span className="text-xl">🎡</span>
      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[9px] font-bold min-w-[16px] h-[16px] px-0.5 flex items-center justify-center rounded-full animate-pulse">
        {spins > 9 ? "9+" : spins}
      </span>
    </button>
  );
}

/* ════════════════════════════════════════════════════════════════ */
export default function MobileBottomBar() {
  const [menu, setMenu]   = useState(false);
  const [notifs, setNotifs]       = useState(0);
  const [wheelSpins, setWheelSpins] = useState(0);
  const location = useLocation();
  const { cart } = useCart();
  const user = getStoredUser();

  /* giros de ruleta disponibles */
  useEffect(() => {
    if (!user) return;
    API.get("/wheel/spins-available")
      .then((r) => setWheelSpins(r.data?.data?.total ?? 0))
      .catch(() => {});
  // eslint-disable-next-line
  }, []);

  /* notificaciones */
  useEffect(() => {
    if (!user) return;
    API.get("/notifications/unread-count")
      .then((r) => setNotifs(r.data?.data?.count ?? r.data?.count ?? 0))
      .catch(() => {});
  // eslint-disable-next-line
  }, [location.pathname]);

  useEffect(() => {
    if (!menu) return;
    API.get("/notifications/unread-count")
      .then((r) => setNotifs(r.data?.data?.count ?? r.data?.count ?? 0))
      .catch(() => {});
  }, [menu]);

  const accountBtn = <AccountBtn notifCount={notifs} onClick={() => setMenu(true)} />;
  const drawer     = <Drawer open={menu} onClose={() => setMenu(false)} />;

  /* ── RYDER ───────────────────────────────────────────────────── */
  if (user?.role === "ryder") return (
    <>
      <FloatingCart count={cart.length} bottom="bottom-28" />
      <FloatingWheel spins={wheelSpins} />
      <Bar>
        <Btn  icon={Home}    label="Inicio"  to="/" exact />
        <Btn  icon={Package} label="Pedidos" to="/my-orders" />
        <CenterBtn icon={Motorbike} label="Mi Panel" to="/ryder" color="amber" />
        <Btn  icon={MapPin}  label="Mapa"    to="/map" />
        {accountBtn}
      </Bar>
      {drawer}
    </>
  );

  /* ── COMPANY ─────────────────────────────────────────────────── */
  if (user?.role === "company") return (
    <>
      <FloatingCart count={cart.length} bottom="bottom-28" />
      <FloatingWheel spins={wheelSpins} />
      <Bar>
        <Btn  icon={Home}          label="Inicio"  to="/" exact />
        <Btn  icon={ClipboardList} label="Pedidos" to="/company/orders" />
        <CenterBtn icon={Building} label="Mi Panel" to="/company" color="blue" />
        <NotifBtn  notifCount={notifs} />
        <AccountBtn notifCount={0} onClick={() => setMenu(true)} />
      </Bar>
      {drawer}
    </>
  );

  /* ── DELIVERY ────────────────────────────────────────────────── */
  if (user?.role === "delivery") return (
    <>
      <FloatingWheel spins={wheelSpins} />
      <Bar>
        <Btn  icon={Home}  label="Inicio"  to="/" exact />
        <Btn  icon={Users} label="Ryders"  to="/delivery/ryders" />
        <CenterBtn icon={Truck} label="Mi Panel" to="/delivery" color="blue" />
        <Btn  icon={MapPin} label="Mapa"   to="/map" />
        {accountBtn}
      </Bar>
      {drawer}
    </>
  );

  /* ── ADMIN ───────────────────────────────────────────────────── */
  if (user?.role === "admin") return (
    <>
      <FloatingWheel spins={wheelSpins} />
      <Bar>
        <Btn  icon={Home}       label="Inicio" to="/" exact />
        <Btn  icon={Bell}       label="Notifs" to="/notifications" />
        <CenterBtn icon={ShieldUser} label="Admin" to="/admin" color="violet" />
        <Btn  icon={MapPin}     label="Mapa"   to="/map" />
        {accountBtn}
      </Bar>
      {drawer}
    </>
  );

  /* ── USER (sesión activa) ────────────────────────────────────── */
  if (user?.role === "user") return (
    <>
      <FloatingCart count={cart.length} />
      <FloatingWheel spins={wheelSpins} />
      <Bar>
        <Btn  icon={Home}         label="Inicio"  to="/" exact />
        <Btn  icon={BadgePercent} label="Ofertas" to="/ofertas" />
        <CenterBtn icon={ShoppingCart} label="Carrito" to="/cart" color="amber" />
        <Btn  icon={MapPin}       label="Mapa"    to="/map" />
        {accountBtn}
      </Bar>
      {drawer}
    </>
  );

  /* ── GUEST (sin sesión) ──────────────────────────────────────── */
  return (
    <>
      <FloatingCart count={cart.length} />
      <Bar>
        <Btn icon={Home}         label="Inicio"   to="/" exact />
        <Btn icon={BadgePercent} label="Ofertas"  to="/ofertas" />
        <CenterBtn icon={ShoppingCart} label="Carrito" to="/cart" color="amber" />
        <Btn icon={MapPin}       label="Mapa"     to="/map" />
        <Btn icon={LogIn}        label="Ingresar" to="/login" />
      </Bar>
    </>
  );
}
